// eslint-disable-next-line eag/no-external-date-time-format-functions -- Custom format
import {format} from 'date-fns';
import {Box, HStack} from 'platform/foundation';
import {
  convertStringLocaleToDateFns,
  getDateValueFromDayMonth,
  useDayMonthFormatter,
  useLocale,
} from 'platform/locale';
import {cva} from 'platform/shared';
import {match} from 'ts-pattern';

import {useEffect, useRef} from 'react';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {DayMonthType, suffixTestId, TestIdProps} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {useAnimatedPopper} from '../AnimatedPopper/hooks/useAnimatedPopper';
import {TextInput} from '../TextInput/TextInput';
import {months} from './constants/months';
import {useMonthName} from './hooks/useMonthName';
import {getDropdownDays} from './utils/getDropdownDays';
import {isDayInMonth} from './utils/isDayInMonth';

const DEFAULT_MONTH = 1;
const DEFAULT_DAY = 1;

const selectionVariants = cva('rounded-[var(--radius-small)] px-4 py-2 text-center', {
  variants: {
    state: {
      default:
        'cursor-pointer bg-[initial] text-[var(--color-primary)] hover:bg-[var(--color-palettes-blue-10-100)] hover:text-[var(--color-palettes-blue-80-100)]',
      selected:
        'cursor-pointer bg-[var(--color-palettes-blue-10-100)] text-[var(--color-palettes-blue-80-100)] hover:bg-[var(--color-palettes-blue-10-100)] hover:text-[var(--color-palettes-blue-80-100)]',
      disabled: 'cursor-not-allowed bg-[initial] text-[var(--color-palettes-neutral-900-40)]',
      disabledSelected:
        'cursor-not-allowed bg-[var(--color-palettes-blue-10-100)] text-[var(--color-palettes-neutral-900-40)]',
    },
  },
  defaultVariants: {
    state: 'default',
  },
});

const listClassName =
  'flex h-60 w-full flex-col gap-px overflow-y-auto p-4 [scrollbar-gutter:stable]';

function getSelectionState(isSelected: boolean, isDisabled: boolean) {
  return match({isSelected, isDisabled})
    .with({isDisabled: true, isSelected: true}, () => 'disabledSelected' as const)
    .with({isDisabled: true}, () => 'disabled' as const)
    .with({isSelected: true}, () => 'selected' as const)
    .otherwise(() => 'default' as const);
}

export interface DayMonthPickerProps extends FormControlProps<DayMonthType | null>, TestIdProps {
  isRecommended?: boolean;
}

export function DayMonthPicker(props: DayMonthPickerProps) {
  const {language} = useLocale();
  const {getMonthName} = useMonthName();
  const dayMonthFormatter = useDayMonthFormatter();

  const {popperProps, Popper, openPopper, closePopper, referenceRef, isOpen} = useAnimatedPopper({
    placement: 'top-start',
    gutter: 0,
    // Re-anchor to the input on scroll/resize; without it the popper is positioned once and
    // stays stranded at its initial screen coordinates while the page scrolls.
    autoUpdateOptions: 'default',
  });

  const isInvalid = props.isInvalid || !!props.errorMessage;

  const monthRef = useRef<HTMLDivElement>(null);
  const dayRef = useRef<HTMLDivElement>(null);

  const days = getDropdownDays(props.value?.month);

  useEffect(
    function scrollSelectedIntoViewOnOpen() {
      if (isOpen) {
        monthRef.current?.scrollIntoView({block: 'center'});
        dayRef.current?.scrollIntoView({block: 'center'});
      }
    },
    [isOpen]
  );

  const onMonthSelect = (month: number) => {
    const selectedDay = props.value?.day;

    let day = isNil(selectedDay) ? DEFAULT_DAY : selectedDay;

    day = isDayInMonth(day, month) ? day : DEFAULT_DAY;

    props.onChange?.({month, day});
  };

  const onDaySelect = (day: number) => {
    const selectedMonth = props.value?.month;

    if (isNil(selectedMonth)) {
      return;
    }

    props.onChange?.({month: selectedMonth, day});
    closePopper();
  };

  return (
    <>
      <div ref={referenceRef}>
        <TextInput
          leftIcon="action/today"
          onClick={props.isDisabled ? undefined : openPopper}
          isRequired={props.isRequired}
          isDisabled={props.isDisabled}
          value={props.value ? dayMonthFormatter('dateMedium', props.value) : null}
          placeholder="dd MMMM"
          label={props.label}
          tooltip={props.tooltip}
          isInvalid={isInvalid}
          errorMessage={props.errorMessage}
          helperText={props.helperText}
          isRecommended={props.isRecommended}
          data-testid={suffixTestId('input', props)}
        />
      </div>
      <Popper {...popperProps}>
        <Box paddingTop={1}>
          <Box
            borderRadius="medium"
            boxShadow="elevation_2"
            border="1px solid"
            borderColor="palettes.neutral.40.100"
            backgroundColor="general.white"
            data-testid={suffixTestId('dropdown', props)}
          >
            <HStack width="auto">
              <Box flex={1}>
                <div className={listClassName} data-testid={suffixTestId('monthList', props)}>
                  {months.map((month) => (
                    <div
                      className={selectionVariants({
                        state: getSelectionState(month === props.value?.month, false),
                      })}
                      ref={month === props.value?.month ? monthRef : null}
                      onClick={() => onMonthSelect(month)}
                      key={month}
                    >
                      {getMonthName(month)}
                    </div>
                  ))}
                </div>
              </Box>
              <div className={listClassName} data-testid={suffixTestId('dayList', props)}>
                {days.map((day) => (
                  <div
                    className={selectionVariants({
                      state: getSelectionState(day === props.value?.day, isNil(props.value?.month)),
                    })}
                    ref={day === props.value?.day ? dayRef : null}
                    onClick={() => isNotNil(props.value?.month) && onDaySelect(day)}
                    key={day}
                  >
                    {format(
                      getDateValueFromDayMonth(props.value?.month ?? DEFAULT_MONTH, day),
                      'd',
                      {
                        locale: convertStringLocaleToDateFns(language),
                      }
                    )}
                  </div>
                ))}
              </div>
            </HStack>
          </Box>
        </Box>
      </Popper>
    </>
  );
}
